import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { KeyRound, Save, ShieldCheck, AtSign } from "lucide-react";

export default function AccountSecurityPage() {
  const { user, refresh } = useAuth();

  return (
    <div className="px-6 lg:px-10 py-8" data-testid="account-security-page">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Settings</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Account Security</h1>
        <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
          Update the email and password you use to sign in. Signed in as <span className="font-semibold text-neutral-900">{user?.email}</span>.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <ChangeEmailCard currentEmail={user?.email} onUpdated={refresh} />
          <ChangePasswordCard />
        </div>

        <aside className="border border-neutral-200 p-6 self-start" data-testid="security-tips">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3 flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3" strokeWidth={1.5} /> Security Tips
          </div>
          <ul className="text-sm text-neutral-600 space-y-2 list-disc pl-4">
            <li>Use a unique password — not your CORE Realty or personal email password.</li>
            <li>Length beats complexity. Try a four-word passphrase.</li>
            <li>Changing your email re-issues your session — you stay signed in.</li>
            <li>If you forgot your password, ask your brokerage admin to reset it for you.</li>
          </ul>
        </aside>
      </div>
    </div>
  );
}

function ChangeEmailCard({ currentEmail, onUpdated }) {
  const [form, setForm] = useState({ current_password: "", new_email: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.new_email || form.new_email.toLowerCase() === (currentEmail || "").toLowerCase()) {
      toast.error("Enter a different email than the one you have now");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-email", {
        current_password: form.current_password,
        new_email: form.new_email.toLowerCase().trim(),
      });
      toast.success("Email updated. Use the new address next time you sign in.");
      setForm({ current_password: "", new_email: "" });
      if (onUpdated) await onUpdated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-neutral-200 p-6 space-y-5" data-testid="change-email-form">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 flex items-center gap-1.5">
        <AtSign className="h-3 w-3" strokeWidth={1.5} /> Change Email Address
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Current Email</Label>
        <div className="mt-2 font-mono text-sm text-neutral-700 px-3 py-2 border border-neutral-200 bg-neutral-50">{currentEmail}</div>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">New Email Address</Label>
        <Input
          type="email"
          value={form.new_email}
          onChange={(e) => setForm({ ...form, new_email: e.target.value })}
          required
          autoComplete="email"
          className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 font-mono"
          data-testid="new-email-input"
        />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Confirm with Current Password</Label>
        <Input
          type="password"
          value={form.current_password}
          onChange={(e) => setForm({ ...form, current_password: e.target.value })}
          required
          autoComplete="current-password"
          className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
          data-testid="change-email-current-password-input"
        />
      </div>
      <div className="flex justify-end pt-2 border-t border-neutral-200">
        <Button type="submit" disabled={saving} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="change-email-submit">
          <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />
          {saving ? "Updating…" : "Update email"}
        </Button>
      </div>
    </form>
  );
}

function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated. You'll stay signed in on this device.");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-neutral-200 p-6 space-y-5" data-testid="change-password-form">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 flex items-center gap-1.5">
        <KeyRound className="h-3 w-3" strokeWidth={1.5} /> Change Password
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Current Password</Label>
        <Input
          type="password"
          value={form.current_password}
          onChange={(e) => setForm({ ...form, current_password: e.target.value })}
          required
          autoComplete="current-password"
          className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
          data-testid="current-password-input"
        />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">New Password</Label>
        <Input
          type="password"
          value={form.new_password}
          onChange={(e) => setForm({ ...form, new_password: e.target.value })}
          required
          minLength={6}
          autoComplete="new-password"
          className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
          data-testid="new-password-input"
        />
        <p className="text-xs text-neutral-500 mt-1">At least 6 characters. Use a passphrase you can remember.</p>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Confirm New Password</Label>
        <Input
          type="password"
          value={form.confirm_password}
          onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
          required
          minLength={6}
          autoComplete="new-password"
          className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
          data-testid="confirm-password-input"
        />
      </div>
      <div className="flex justify-end pt-2 border-t border-neutral-200">
        <Button type="submit" disabled={saving} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="change-password-submit">
          <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />
          {saving ? "Updating…" : "Update password"}
        </Button>
      </div>
    </form>
  );
}
